/**
 * Custom hook for landing page state management using Redux
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserMode, AgeRange, LandingPageState, LandingPageActions } from "../types/landing";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  setUserMode,
  setAgeRange,
  setHasConsented,
  setLoading,
  selectUserMode,
  selectAgeRange,
  selectHasConsented,
  selectCanContinue,
} from "../store/slices/appSlice";

interface UseLandingPageStateProps {
  initialState?: Partial<LandingPageState>;
}

export function useLandingPageState({ initialState }: UseLandingPageStateProps = {}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get state from Redux
  const selectedMode = useAppSelector(selectUserMode);
  const selectedAge = useAppSelector(selectAgeRange);
  const hasConsented = useAppSelector(selectHasConsented);
  const canContinue = useAppSelector(selectCanContinue);

  const handleModeSelect = useCallback(
    (mode: UserMode) => {
      dispatch(setUserMode(mode));

      if (mode !== "explorer") {
        // Navigate directly to home page for adult/admin modes
        dispatch(setLoading(true));
        if (mode === "adult") {
          navigate("/home");
        } else if (mode === "admin") {
          navigate("/admin/home");
        }
        // Stop loading after navigation
        setTimeout(() => dispatch(setLoading(false)), 100);
      }
    },
    [navigate, dispatch]
  );

  const resetState = useCallback(() => {
    dispatch(setUserMode(null));
    dispatch(setAgeRange(null));
    dispatch(setHasConsented(false));
  }, [dispatch]);

  const getContinuePath = useCallback(() => {
    if (selectedMode === "explorer") {
      return `/explorer/home`;
    }
    return "/home";
  }, [selectedMode]);

  const actions: LandingPageActions = {
    setSelectedMode: (mode: UserMode | null) => dispatch(setUserMode(mode)),
    setSelectedAge: (age: AgeRange | null) => dispatch(setAgeRange(age)),
    setHasConsented: (consented: boolean) => dispatch(setHasConsented(consented)),
    handleModeSelect,
  };

  const state: LandingPageState = {
    selectedMode,
    selectedAge,
    hasConsented,
  };

  return {
    state,
    actions,
    canContinue,
    getContinuePath,
    resetState,
  };
}
